import { requiredStringSchema } from "@gigpro/ui/Form/utils";
import { browserTimezone, convertToTimezone, RecurrenceWithRepeat } from "@gigpro/utils/datetime";
import {
  GigAudienceTypeEnum,
  type StafferGigViewReadonlyRetrieveSerializerV2,
  type StafferGigViewReadonlySerializerV2,
} from "@sidegigllc/api/client";
import { eventSchema } from "api/resources/event";
import { attachmentTypeSchema, fileAttachmentSchema, tipPolicySchema } from "api/resources/shared";
import * as z from "zod/mini";
import { locationAddressSchema } from "./LocationAddress";
import { makeApiResource } from "./resource";
import {
  billingSourceSchema,
  gigCancellationReasonSchema,
  gigStatusSchema,
  gigTypeSchema,
  parkingInstructionsFieldsSchema,
} from "./shared";
import type { StafferGig, StafferGigDetail, StafferGigV2 } from "./StafferGig";
import { uniformSelectionSchema } from "./uniforms";

const overfillTypeSchema = z.enum(["MANUAL", "AUTOMATIC", "DISABLED"]);

export type RecurrenceScope = "single" | "recurring";

export const toContactPhoneE164 = (phone: string) => (phone.startsWith("+1") ? phone : `+1${phone}`);

const blockProInputSchema = z.object({
  gigster_id: z.number(),
  location_ids: z.array(z.number()),
  reason: z.enum([
    "CANCELLED_CONFIRMED_PRO",
    "CANCELLED_CALLED_OUT",
    "COMPLETED_GIG_AFTER_PAYMENT",
    "BLOCKED_BY_GIGPRO_ADMIN",
  ]),
  feedback: z.nullish(z.string()),
});
export type BlockProInput = z.infer<typeof blockProInputSchema>;

export const gigCancellationPostPayloadSchema = z.object({
  gig_id: z.number(),
  reason: gigCancellationReasonSchema,
  explanation: requiredStringSchema,
  block_pro_data: z.optional(blockProInputSchema),
  rating: z.optional(z.number()),
  include_recurring_gigs: z.optional(z.boolean()),
});
export type GigCancellationPostPayload = z.infer<typeof gigCancellationPostPayloadSchema>;

export function postProcessServerGigV2(
  { headshots, is_working_interview, ...gig }: StafferGigV2,
  { skipTzConversion = false }: { skipTzConversion?: boolean } = {},
): StafferGig {
  return {
    ...gig,
    type: is_working_interview ? "WORKING_INTERVIEW" : gig.type,
    start_time: skipTzConversion ? gig.start_time : convertToTimezone(gig.start_time, gig.location.timezone),
    end_time: skipTzConversion ? gig.end_time : convertToTimezone(gig.end_time, gig.location.timezone),
    requested_pros_count:
      gig.overfill_type !== "DISABLED" && gig.original_allowed_confirmed_pros
        ? gig.original_allowed_confirmed_pros
        : gig.allowed_confirmed_pros,
    manual_overfill_pros_count:
      gig.manual_overfill_pros_count ??
      (gig.overfill_type !== "DISABLED" && gig.original_allowed_confirmed_pros
        ? gig.allowed_confirmed_pros - gig.original_allowed_confirmed_pros
        : 0),
    stats: {
      ...gig.stats,
      total_applications_count: gig.stats.total_applications_count ?? 0,
      selected_applications_count: gig.stats.selected_applications_count ?? 0,
      cancelled_applications_count: gig.stats.cancelled_applications_count ?? 0,
      confirmed_pros_count: gig.stats.confirmed_pros_count ?? 0,
      open_applications_count:
        (gig.stats.total_applications_count ?? 0) -
        (gig.stats.cancelled_applications_count ?? 0) -
        (gig.stats.selected_applications_count ?? 0),
    },
    headshots: headshots ?? [],
    attachments: gig.attachments?.map((attachment) => ({ ...attachment, type: attachment.type ?? "DEFAULT" })),
  };
}

/**
 * Adapt a generated V2 gig-list row to the FE `StafferGig` shape consumers expect. The generated client
 * already returns coerced numbers/dates and (post CORE-5639) aligned stats/headshots, so this only
 * re-derives the two computed fields absent from the wire: the `WORKING_INTERVIEW` display type and
 * `requested_pros_count`, plus `stats.open_applications_count`.
 */
export function stafferGigV2ToStafferGig(gig: StafferGigViewReadonlySerializerV2): StafferGig {
  return {
    ...gig,
    // The wire row can omit `location_address` entirely (FE `StafferGig` types it optional); only build the
    // coerced shape when present, otherwise pass the absence through.
    location_address: gig.location_address,
    // `settings` has no coerced wire counterpart — the generated shape is unrelated to the FE `GigSettings`.
    settings: gig.settings as unknown as StafferGig["settings"],
    type: gig.is_working_interview ? "WORKING_INTERVIEW" : gig.type,
    requested_pros_count:
      gig.overfill_type !== "DISABLED" && gig.original_allowed_confirmed_pros
        ? gig.original_allowed_confirmed_pros
        : gig.allowed_confirmed_pros,
    // Generated V2 serializers omit `manual_overfill_pros_count`; derive it from the allowed/original delta
    // so the edit dialog's overfill selector doesn't default to 0 and silently drop the current overfill.
    manual_overfill_pros_count:
      gig.overfill_type !== "DISABLED" && gig.original_allowed_confirmed_pros
        ? gig.allowed_confirmed_pros - gig.original_allowed_confirmed_pros
        : 0,
    stats: {
      ...gig.stats,
      total_applications_count: gig.stats.total_applications_count ?? 0,
      selected_applications_count: gig.stats.selected_applications_count ?? 0,
      confirmed_pros_count: gig.stats.confirmed_pros_count ?? 0,
      confirmed_auto_matched_pros_count: gig.stats.confirmed_auto_matched_pros_count ?? 0,
      cancelled_applications_count: gig.stats.cancelled_applications_count ?? 0,
      total_payments_count: gig.stats.total_payments_count ?? 0,
      failed_payments_count: gig.stats.failed_payments_count ?? 0,
      error_payments_count: gig.stats.error_payments_count ?? 0,
      submitted_payments_count: gig.stats.submitted_payments_count ?? 0,
      paid_pros_count: gig.stats.paid_pros_count ?? 0,
      open_applications_count:
        (gig.stats.total_applications_count ?? 0) -
        (gig.stats.cancelled_applications_count ?? 0) -
        (gig.stats.selected_applications_count ?? 0),
    },
  };
}

/**
 * Adapt a generated V2 gig-detail (retrieve) row to the FE `StafferGigDetail` shape. Reuses the list
 * derivations and adds the detail-only fields (contact, parking, requirements); timezone conversion is
 * handled upstream by `stafferGigsV2RetrieveWithTransform`.
 */
export function stafferGigV2DetailToStafferGigDetail(
  gig: StafferGigViewReadonlyRetrieveSerializerV2,
): StafferGigDetail {
  return {
    ...stafferGigV2ToStafferGig(gig),
    contact_name: gig.contact_name,
    contact_phone: gig.contact_phone,
    parking_expense_type: gig.parking_expense_type,
    parking_instructions: gig.parking_instructions,
    projected_push_back_close_time: gig.projected_push_back_close_time,
    requirements: gig.requirements,
    // Generated `GigAttachment.type` is optional; default missing types to `DEFAULT` so legacy attachments
    // aren't filtered out of the details dialog and attachment editor (which key off `type === "DEFAULT"`).
    attachments: gig.attachments?.map((attachment) => ({
      ...attachment,
      type: attachment.type ?? "DEFAULT",
    })) as unknown as StafferGigDetail["attachments"],
  };
}

const gigCommonFieldsSchema = z.object({
  status: gigStatusSchema,
  start_time: z.coerce.date(),
  end_time: z.coerce.date(),
  allowed_confirmed_pros: z.number(),

  skill_id: z.number(),

  location_address_id: z.nullish(z.number()),
  location_id: z.number(),
  address_line_1: z.string(),
  city: z.string(),
  zip_code: z.string(),
  state_code: z.string(),

  description: z.string(),
  no_experience_required: z.boolean(),
  special_inst: z.nullable(z.optional(z.string())),
  wage_amount: z.coerce.number(),

  type: z._default(gigTypeSchema, "MARKETPLACE"),

  auto_select_enabled: z.nullish(z.boolean()),
  attachments: z.nullish(z.array(fileAttachmentSchema)),
  unpaid_break_minutes: z.nullish(z.number()),

  onsite_manager_id: z.nullish(z.number()),

  po_number: z.nullish(z.string()),
  // Business's "Does this shift include tips?" answer (WORK-8452). `null` = unanswered; the API rejects
  // null with a 400 on `tip_policy` when the skill's `tips_required` is on.
  tip_policy: z.nullish(tipPolicySchema),

  ...z.partial(parkingInstructionsFieldsSchema).shape,
});

export const gigPostPayloadSchema = z.extend(z.omit(gigCommonFieldsSchema, { status: true }), {
  use_location_address: z.boolean(),
  timezone: z.string(),
  // Write payload never sends null for these (the read schema allows it); keep them non-null to match the
  // generated create request.
  auto_select_enabled: z.optional(z.boolean()),
  unpaid_break_minutes: z.optional(z.number()),
  selected_requirement_ids: z.optional(z.array(z.number())),
  // Write-only uniform snapshot (backend CORE-6240).
  uniform: z.optional(uniformSelectionSchema),
  recurrence: z.optional(RecurrenceWithRepeat),
  event_id: z.optional(z.string()),
  attachments: z.optional(z.array(z.object({ id: z.string(), type: attachmentTypeSchema }))),
  is_auto_post_network_to_marketplace_enabled: z.optional(z.boolean()),
  audience: z.optional(z.object({ type: z.enum(GigAudienceTypeEnum), pro_ids: z.optional(z.array(z.number())) })),
  offer_unfilled_to_all: z.optional(z.boolean()),
  contact_name: z.string(),
  contact_phone: z.string(),
});
export type GigPostPayload = z.infer<typeof gigPostPayloadSchema>;

const gigSettingSchema = z.object({ display_name: z.string(), description: z.string() });

export const gigListSchema = z.extend(gigCommonFieldsSchema, {
  id: z.number(),
  created_ts: z.coerce.date(),
  location: makeApiResource({
    image_public_id: z.nullish(z.string()),
    market_id: z.number(),
    name: z.string(),
    timezone: z.catch(z.string(), browserTimezone),
    billing_source: billingSourceSchema,
    organization_id: z.string(),
  }),
  skill: z.string(),
  skill_id: z.number(),
  background_check_required: z._default(z.boolean(), false),
  background_checks_fee_percentage: z.nullish(z.number()),
  chat_token: z.nullish(z.string()),
  bundle_id: z.nullish(z.number()),
  number_of_gigs_in_bundle: z.nullish(z.number()),
  auto_select_enabled: z.nullish(z.boolean()),
  is_recurring: z.nullish(z.boolean()),
  original_allowed_confirmed_pros: z.nullish(z.number()),
  overfill_type: z.catch(overfillTypeSchema, "DISABLED"),
  manual_overfill_pros_count: z.nullish(z.number()),
  boosted_gig_bonus: z.nullish(z.coerce.number()),
  event: z.nullish(eventSchema),
  require_certifications: z._default(z.boolean(), false),
  is_working_interview: z._default(z.boolean(), false),

  headshots: z.pipe(
    z.nullish(z.array(z.string())),
    z.transform((headshots) => headshots ?? []),
  ),
  is_checkin_required: z.optional(z.boolean()),
  checkin_code: z.string(),
  location_address: z.nullish(locationAddressSchema),

  stats: z.pipe(
    z.object({
      total_applications_count: z._default(z.number(), 0),
      selected_applications_count: z._default(z.number(), 0),
      confirmed_pros_count: z._default(z.number(), 0),
      confirmed_auto_matched_pros_count: z._default(z.number(), 0),
      cancelled_applications_count: z._default(z.number(), 0),
      total_payments_count: z._default(z.number(), 0),
      failed_payments_count: z._default(z.number(), 0),
      error_payments_count: z._default(z.number(), 0),
      submitted_payments_count: z._default(z.number(), 0),
      paid_pros_count: z._default(z.number(), 0),
      flagged_time_punches_count: z.optional(z.number()),
      needs_attention: z.optional(z.boolean()),
    }),
    z.transform((stats) => ({
      ...stats,
      open_applications_count:
        stats.total_applications_count - stats.cancelled_applications_count - stats.selected_applications_count,
    })),
  ),
  settings: z.object({
    is_checkin_required: gigSettingSchema,
    auto_select_enabled: gigSettingSchema,
    is_auto_post_network_to_marketplace_enabled: gigSettingSchema,
  }),
});
export type Gig = z.infer<typeof gigListSchema>;

export const offerableGigSchema = z.extend(
  z.pick(gigListSchema, { id: true, start_time: true, end_time: true, description: true, skill_id: true }),
  {
    wage_amount: z.nullable(z.coerce.number()),
    skill: z.string(),
    location: z.pick(gigListSchema.shape.location, { id: true, image_public_id: true, name: true, timezone: true }),
    has_applied: z.boolean(),
    is_selected: z.nullable(z.boolean()),
    is_confirmed: z.nullable(z.boolean()),
    is_cancelled: z.nullable(z.boolean()),
    is_filled: z.nullable(z.optional(z.boolean())),
    is_high_demand: z._default(z.boolean(), false),
    high_demand_surcharge_multiplier_applied: z._default(z.nullable(z.coerce.number()), null),
  },
);
export type OfferableGig = z.infer<typeof offerableGigSchema>;
